import { Injectable } from '@angular/core';
import { QueryEntity, combineQueries } from '@datorama/akita';
import { CartStore, CartState } from './cart.store';
import { MarketplaceExtendedQuery } from '../marketplace/marketplace.extended.query';
import { PortalProduct } from '../portal-products/models/portal-product.model';
import { MarketplaceItemExtended } from '../marketplace/models/marketplace-item-extended.model';
import { MemberQuery } from '../../account/member/member.query';
import { map, tap } from 'rxjs/operators';
import { Cart, MarketplaceItemCart } from './models/cart.model';
import { MarketplaceAttributeCartItem } from '../marketplace/models/basket/marketplace-attribute-cart-item.model';
import { MarketplaceBasketCustomFieldModel } from '../marketplace/models/basket/marketplace-basket-customfield.model';
import { isEqual } from '../../shared/utility-functions';

/**
 * This cart query will handle the queries made
 * to the cart store for both marketplace items
 * and portal products.
 */
@Injectable({ providedIn: 'root' })
export class CartQuery extends QueryEntity<CartState> {
  constructor(protected store: CartStore, private marketPlaceQuery: MarketplaceExtendedQuery, private memberQuery: MemberQuery) {
    super(store);
  }

  /**
   * @returns Combined queries from
   * both the cart store and the marketplace store.
   * This combine query is required to get all information
   * on the marketplace item added to cart store.
   */
  selectAllMarketplaceCartData() {
    return combineQueries([
      this.selectAll({
        filterBy: [
          (entity) =>
            this.instanceOfMarketplaceItem(entity.productData) && entity.memberId == this.memberQuery.getUserContext().memberId,
        ],
      }),
      this.marketPlaceQuery.selectExtendedMarketplaceItems(),
    ]).pipe(
      map(([cartItems, productItems]) => {
        if (!cartItems || !productItems) {
          return undefined;
        }

        return cartItems.map((cartItem) => {
          return {
            ...cartItem,
            productData: productItems.find(
              (marketplaceProduct) => marketplaceProduct.Id === (cartItem.productData as MarketplaceItemExtended).Id
            ) as MarketplaceItemExtended,
          } as MarketplaceItemCart;
        });
      }),
      tap((cart) => {
        if (cart) {
          this.verifyCartItems(cart);
        }
      })
    );
  }

  /**
   * Calculate items within the cart for the current
   * member ID and only for marktetplace items.
   */
  updateTotalItems() {
    return this.selectAll({
      filterBy: [
        (entity) =>
          this.instanceOfMarketplaceItem(entity.productData) && entity.memberId == this.memberQuery.getUserContext().memberId,
      ],
    }).pipe(map((items) => items.reduce((accumVal, item) => accumVal + item.purchaseQuantity, 0)));
  }

  /**
   * @returns Calculate total points value of all items added and the quantities
   * (only for marketplace items within the cart), total points value is returned.
   */
  updateBasketPointsTotal() {
    const totalPoints = this.selectAllMarketplaceCartData().pipe(
      map((items) => {
        if (items) {
          return items.reduce((accumVal, item) => accumVal + item.purchaseQuantity * item.productData.PointValue, 0);
        }
        return;
      })
    );
    return totalPoints;
  }

  /**
   * Select all portal products added to
   * the cart store.
   */
  selectAllSubscriptionCartData() {
    return this.selectAll({
      filterBy: [
        (entity) =>
          this.instanceOfPortalProduct(entity.productData) && entity.memberId == this.memberQuery.getUserContext().memberId,
      ],
    });
  }

  /**
   * Calculates the total cost of all
   * portal products within the cart.
   */
  calculateTotalCost() {
    const totalCost = this.selectAllSubscriptionCartData().pipe(
      map((items) => items.reduce((accumVal, item) => accumVal + (item.productData as PortalProduct).MerchantPrice, 0))
    );
    return totalCost;
  }

  /**
   * @param object Object passed to be checked.
   * @returns Returns the portal product Id.
   * This is a check to see whether a object in the cart store is
   * an instance of PortalProduct
   */
  instanceOfPortalProduct(object: any): object is PortalProduct {
    return 'ProductId' in object;
  }

  /**
   * @param object Object passed to be checked.
   * @returns Returns the martketplace item Id.
   * This is a check to see whether a object in the cart store is
   * an instance of MarketplaceItemExtended
   */
  instanceOfMarketplaceItem(object: any): object is MarketplaceItemExtended {
    return 'Id' in object;
  }

  /**
   * @param items array of items within the store
   * to be cleared.
   * @returns To clear all items from the cart store,
   * which would be called on the payment processs.
   */
  removeItems(items: Cart[]) {
    this.store.remove(items.map((cartItem) => cartItem.id));
  }
  /**
   * @returns To get all marketplace items
   * within the cart store, for the current
   * member ID.
   */
  getMemberMarketplaceCart() {
    return this.getAll({
      filterBy: [
        (entity) =>
          this.instanceOfMarketplaceItem(entity.productData) && entity.memberId == this.memberQuery.getUserContext().memberId,
      ],
    });
  }
  /**
   * @returns To get all portal product items
   * within the cart store, for the current
   * member ID.
   */
  getMemberPortalProductCart() {
    return this.getAll({
      filterBy: [
        (entity) =>
          this.instanceOfPortalProduct(entity.productData) && entity.memberId == this.memberQuery.getUserContext().memberId,
      ],
    });
  }
  /**
   * @param attributeMappingId Attribute Mapping ID of the desired item, if any
   * @param customFields Custom Fields Values of the desired item, if any
   * @returns To get specifc martketplace item
   * within the cart store, for the current
   * member ID.
   */
  getMemberMarketplaceCartItem(itemId: number, attributeMappingId?: number, customFields?: MarketplaceBasketCustomFieldModel[]) {
    return this.getAll({
      filterBy: [
        (entity) =>
          this.instanceOfMarketplaceItem(entity.productData) &&
          (entity.productData as MarketplaceItemExtended).Id == itemId &&
          (entity.attributes as MarketplaceAttributeCartItem).AttributeMappingId == attributeMappingId &&
          (customFields ? isEqual(entity.customFields, customFields) : !entity.customFields) &&
          entity.memberId == this.memberQuery.getUserContext().memberId,
      ],
    })[0];
  }

  /**
   * @param attributeMappingId Attribute Mapping ID of the desired item, if any
   * @returns To get purchase quantity of martketplace item
   * within the cart store, for the current member ID.
   * Cart items are differenciated by attributes and custom fields, but custom fields are not relevant to stock.
   */
  getCartItemPurchaseQuantity(itemId: number, attributeMappingId?: number) {
    return this.getAll({
      filterBy: [
        (entity) =>
          this.instanceOfMarketplaceItem(entity.productData) &&
          (entity.productData as MarketplaceItemExtended).Id == itemId &&
          (entity.attributes as MarketplaceAttributeCartItem).AttributeMappingId == attributeMappingId &&
          entity.memberId == this.memberQuery.getUserContext().memberId,
      ],
    }).reduce((a, b) => a + (b['purchaseQuantity'] || 0), 0);
  }

  /**
   * @returns To get specifc portal product item
   * within the cart store, for the current
   * member ID.
   */
  getMemberPortalProductCartItem(portalProductId: number) {
    return this.getAll({
      filterBy: [
        (entity) =>
          this.instanceOfPortalProduct(entity.productData) &&
          (entity.productData as PortalProduct).ProductId == portalProductId &&
          entity.memberId == this.memberQuery.getUserContext().memberId,
      ],
    })[0];
  }

  /**
   * @param itemId The marketplace item ID.
   * @param attributeMappingId The selected attribute ID from the marketplace item.
   * @returns Select marketplace item in the cart, which
   * is used to disable the add to cart button preventing
   * more items being added than possible.
   */
  selectMemberMarketplaceCartItem(itemId: number, attributeMappingId?: number) {
    return this.selectAllMarketplaceCartData().pipe(
      map((cartItems) => {
        if (cartItems) {
          return cartItems.filter((cartItem) => {
            const matchingIDs = cartItem.productData.Id == itemId;
            let matchingAttributes = true;
            if (attributeMappingId && cartItem.attributes) {
              matchingAttributes = cartItem.attributes.AttributeMappingId == attributeMappingId;
            }
            return matchingIDs && matchingAttributes;
          })[0];
        }
        return;
      })
    );
  }

  /**
   * @param productId The portal product ID.
   * @returns Select portal product item in the cart,
   * use to prevent more portal product items being added than possible.
   */
  selectMemberPortalProductCartItem(productId: number) {
    return this.selectAllSubscriptionCartData().pipe(
      map((cartItems) => cartItems.filter((cartItem) => (cartItem.productData as PortalProduct).ProductId == productId)[0])
    );
  }

  /**
   * Verify and Remove any items within the users cart
   * which are no longer available (removed).
   * @param cart array items within cart store
   * @returns updated points total of items in
   * cart.
   */
  verifyCartItems(cart: (Cart | undefined)[]) {
    cart.forEach((item) => {
      if (item && !item.productData) {
        this.store.remove(item.id);
      }
    });
  }
}
